/* eslint-disable */

import { useRef, useCallback, useState, useEffect } from 'react';
import ReactPlayer from 'react-player';
import { HOST, PORT } from '../../environment';

/**
 * @param {object} props
 * @param {string} props.webcamUrl
 * @param {(status: 'loading'|'playing'|'error') => void} [props.onStatusChange]
 */
export function WebCamera({ webcamUrl, onStatusChange }) {
  const videoRef = useRef(null);
  const playerRef = useRef(null);
  const [hasError, setHasError] = useState(false);
  const statusRef = useRef(null);
  const liveConfirmedRef = useRef(false);
  const slateWatchRef = useRef(null);

  const emitStatus = useCallback(
    status => {
      if (statusRef.current === status) return;
      statusRef.current = status;
      onStatusChange?.(status);
    },
    [onStatusChange]
  );

  const clearSlateWatch = useCallback(() => {
    const watch = slateWatchRef.current;
    if (!watch) return;
    clearTimeout(watch.timer);
    slateWatchRef.current = null;
  }, []);

  const markPlaying = useCallback(() => emitStatus('playing'), [emitStatus]);

  const confirmLive = useCallback(() => {
    liveConfirmedRef.current = true;
    clearSlateWatch();
    markPlaying();
  }, [clearSlateWatch, markPlaying]);

  const markOffline = useCallback(() => {
    clearSlateWatch();
    setHasError(true);
    emitStatus('error');
  }, [clearSlateWatch, emitStatus]);

  useEffect(() => {
    setHasError(false);
    statusRef.current = null;
    liveConfirmedRef.current = false;
    clearSlateWatch();
    emitStatus(webcamUrl ? 'loading' : 'error');
  }, [webcamUrl, emitStatus, clearSlateWatch]);

  useEffect(() => () => clearSlateWatch(), [clearSlateWatch]);

  const isIOS =
    /iPhone|iPad|iPod/i.test(navigator.userAgent) ||
    (typeof window !== 'undefined' && window.Capacitor?.getPlatform?.() === 'ios');
  const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);

  /**
   * Imou live playlists are a few seconds long and grow as segments arrive.
   * hls.js 0.12 reports that window as a finite duration (often 6–12s).
   * A fixed short duration is the offline error slate.
   */
  const noteDuration = useCallback(
    durationSec => {
      if (liveConfirmedRef.current) return;
      if (durationSec === Infinity || (Number.isFinite(durationSec) && durationSec >= 60)) {
        confirmLive();
        return;
      }
      if (!Number.isFinite(durationSec) || durationSec <= 0 || durationSec >= 60) {
        return;
      }
      const watch = slateWatchRef.current;
      if (watch && durationSec > watch.duration + 0.25) {
        confirmLive();
        return;
      }
      if (!watch) {
        const timer = setTimeout(() => {
          if (!liveConfirmedRef.current) markOffline();
        }, 5000);
        slateWatchRef.current = { duration: durationSec, timer };
      }
    },
    [confirmLive, markOffline]
  );

  const readPlayerDuration = useCallback(() => {
    const video = playerRef.current?.getInternalPlayer?.();
    if (video && typeof video.duration === 'number') {
      noteDuration(video.duration);
    }
  }, [noteDuration]);

  const handleHlsReady = useCallback(() => {
    const hls = playerRef.current?.getInternalPlayer?.('hls');
    if (!hls || hls.__km220LevelBound) {
      readPlayerDuration();
      return;
    }
    hls.__km220LevelBound = true;
    hls.on('hlsLevelLoaded', (_event, data) => {
      const details = data?.details;
      if (!details) return;
      if (details.live) {
        confirmLive();
        return;
      }
      const total = Number(details.totalduration);
      if (Number.isFinite(total) && total > 0 && total < 60) {
        markOffline();
        return;
      }
      confirmLive();
    });
    readPlayerDuration();
  }, [confirmLive, markOffline, readPlayerDuration]);

  const tryPlay = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;
    noteDuration(video.duration);
    if (video.paused) {
      video.play().catch(() => {
        markOffline();
      });
    }
  }, [markOffline, noteDuration]);

  const handleError = useCallback(
    (...args) => {
      const hlsData = args.find(arg => arg && typeof arg === 'object' && 'fatal' in arg);
      if (hlsData && hlsData.fatal === false) return;
      markOffline();
    },
    [markOffline]
  );

  const videoSrc =
    isIOS || isSafari ? `${HOST}:${PORT}/api/webcam/proxy?url=${encodeURIComponent(webcamUrl)}` : webcamUrl;

  if (hasError || !webcamUrl) {
    return null;
  }

  if (isIOS || isSafari) {
    return (
      <video
        ref={videoRef}
        controls
        playsInline
        muted
        autoPlay
        preload="auto"
        style={{ width: '100%', height: '40%', margin: 'auto' }}
        disablePictureInPicture
        disableRemotePlayback
        onLoadedMetadata={tryPlay}
        onCanPlay={tryPlay}
        onPlaying={() => noteDuration(videoRef.current?.duration)}
        onTimeUpdate={() => noteDuration(videoRef.current?.duration)}
        onDurationChange={() => noteDuration(videoRef.current?.duration)}
        onEnded={() => {
          if (!liveConfirmedRef.current) markOffline();
        }}
        onError={handleError}
      >
        <source src={videoSrc} type="application/vnd.apple.mpegurl" />
      </video>
    );
  }

  return (
    <ReactPlayer
      ref={playerRef}
      url={webcamUrl}
      width="100%"
      height="40%"
      style={{ margin: 'auto' }}
      controls
      playsinline
      playing
      muted
      progressInterval={500}
      onReady={handleHlsReady}
      onDuration={noteDuration}
      onProgress={readPlayerDuration}
      onEnded={() => {
        if (!liveConfirmedRef.current) markOffline();
      }}
      onError={handleError}
      config={{
        file: {
          forceHLS: true,
          forceVideo: true,
          hlsVersion: '0.12.4',
          attributes: {
            disablePictureInPicture: true,
          },
        },
      }}
    />
  );
}
